import { useEffect } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useTranslation } from 'react-i18next';
import { getItemDataQuery, sendAnalyticsDataQuery } from '../queries';
import { makeUrlWithLang, prettyNumber } from '../utils/helpers';
import config from '../config';
import { formatToPrettyArea } from '../utils/helpers/common';

export default (id, asPath) => {
  const { protocol, host } = config;
  const {
    t,
    i18n: { language: lang }
  } = useTranslation();
  const hostString = path => makeUrlWithLang({ protocol, host, path, lang });
  const { loading, data, error } = useQuery(getItemDataQuery(id), {
    variables: {
      id
    },
    ssr: false,
    fetchPolicy: 'network-only'
  });
  const [sendAnalyticsData] = useMutation(sendAnalyticsDataQuery, {
    variables: {
      targetId: id,
      targetType: 'ITEM',
      sourceLink: hostString(asPath),
      eventType: 'ITEM_HIT',
      source: 'MOBILE'
    },
    ssr: false
  });
  const item = data?.item || {};
  const maskedPhonesArray = item?.phones?.map(i => i.value);
  const maskedPhones = item?.phones ? maskedPhonesArray : [];
  const itemPath = item?.path;
  const prettyPrice = prettyNumber(item?.price?.value);
  const prettyAreaRoundedUp = Number.isInteger(item?.area?.value)
    ? item?.area?.value
    : Number(item?.area?.value).toFixed(1);
  const prettyArea = formatToPrettyArea(prettyAreaRoundedUp);
  const prettyAreaWithUnits = item?.area
    ? `${prettyArea} ${item?.area?.units}`
    : '';
  const prettyLandArea = formatToPrettyArea(item?.landArea?.value);
  const prettyLandAreaWithUnits = item?.landArea
    ? `${prettyLandArea} ${item?.landArea?.units}`
    : '';
  const showElectronicBanners = ['1', '2', '3', '5'].includes(
    item?.category?.id
  );
  const isEditAvailable = item?.actions?.includes('EDIT');
  const isRemoveAvailable = item?.actions?.includes('REMOVE');

  const isAuthor = item?.isMine;
  const isResidence =
    item?.company?.targetType === 'RESIDENCE' && !!item?.business;
  const isAgency = item?.company?.targetType === 'AGENCY' && !!item?.business;

  const isPublished = item?.state === 'PUBLISHED';
  const isLimited = item?.state === 'AWAITING_PAYMENT';
  const isExpired = item?.state === 'EXPIRED';
  const isRejected = item?.state === 'REJECTED';
  const isPending = item?.state === 'PENDING';
  const isValidated = item?.state === 'VALIDATED';
  const isLeased = item?.leased;
  const isExpiredManually =
    item?.state === 'EXPIRED' && item?.isExpiredManually;
  const paidServices = item?.paidServices || [];
  const phonesSectionNoBottomSpace = !item?.business
    ? !!item?.business
    : !isAuthor ?? (!isAgency && !isResidence);
  const getPriceCurrency = () => {
    if (item?.paidDaily === null || !isLeased) {
      return item?.price?.currency;
    }

    return item?.paidDaily
      ? `${item?.price?.currency}/${t('cards.item_card.per_day')}`
      : `${item?.price?.currency}/${t('cards.item_card.per_month')}`;
  };

  useEffect(() => {
    try {
      if (data?.item && !data?.item.isMine) {
        sendAnalyticsData();
      }
    } catch (e) {
      console.log(e);
    }
  }, [data?.item]);

  return {
    data,
    item,
    loading,
    error,
    maskedPhones,
    isEditAvailable,
    isRemoveAvailable,
    isAuthor,
    isResidence,
    isAgency,
    isPublished,
    isLimited,
    isExpired,
    isRejected,
    isPending,
    isLeased,
    isExpiredManually,
    paidServices,
    itemPath,
    prettyPrice,
    prettyArea,
    prettyAreaWithUnits,
    prettyLandAreaWithUnits,
    showElectronicBanners,
    phonesSectionNoBottomSpace,
    getPriceCurrency,
    isValidated
  };
};
